// Shared formatting helpers for the Fusion PTR page (kept outside components
// so tests can mock components without losing the helpers).

// Relative "updated x ago" label (minutes / hours / days, else locale date).
export const formatUpdatedAgo = (t, updatedAt) => {
  if (!updatedAt) return '';
  const diffMs = Date.now() - new Date(updatedAt).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return '';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('fusionPtr.updatedJustNow', "À l'instant");
  if (minutes < 60) return t('fusionPtr.updatedMinutesAgo', 'Il y a {{count}} min', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('fusionPtr.updatedHoursAgo', 'Il y a {{count}} h', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('fusionPtr.updatedDaysAgo', 'Il y a {{count}} j', { count: days });
  return new Date(updatedAt).toLocaleDateString();
};

// Priority display mapping for Fusion PTR (P1 = Critical, P2 = High, P3 = Medium, P4 = Low).
export const formatPriority = (priority) => {
  if (!priority) return '';
  const str = String(priority).trim().toLowerCase();
  if (['critical', 'critique', 'p1', '1'].includes(str)) return 'P1';
  if (['high', 'haute', 'elevee', 'élevée', 'p2', '2'].includes(str)) return 'P2';
  if (['medium', 'moyenne', 'p3', '3'].includes(str)) return 'P3';
  if (['low', 'faible', 'p4', '4'].includes(str)) return 'P4';
  if (/^p[1-4]$/i.test(str)) return str.toUpperCase();
  return String(priority).toUpperCase();
};

// Alternate legacy measure shapes (mirror matrixReportGenerator fallbacks).
const snapEffort = (m) => Number(m?.estimatedEffort ?? m?.effort ?? m?.charge) || 0;
const snapDeadline = (m) => m?.deadline || m?.echeance || m?.dueDate || '';
const snapOwner = (m) => m?.responsiblePerson || m?.responsibleParty || '';

// Editable PTR Final fields (mirrors the backend whitelist in
// backend/utils/ptrFusionUtils.js applyFinalMeasureOverrides).
export const FINAL_EDITABLE_FIELDS = ['name', 'description', 'category', 'priority', 'budget', 'estimatedEffort', 'responsiblePerson', 'deadline', 'justification'];

// Hydrate stored/computed rows against the frozen snapshot so missing fields
// (name, description, category, priority, budget, effort, owner, deadline)
// are backfilled — mirrors backend hydrateFinalMeasures (ptrFusionUtils.js).
export const hydrateFinalMeasures = (measures = [], snapshot = []) => {
  const byKey = new Map();
  (snapshot || []).forEach((snap) => {
    (snap.measures || []).forEach((m) => {
      const name = m.name || m.title || m.measureName || m.label || '';
      byKey.set(m.key, { ...m, name, analysisName: snap.analysisName });
    });
  });

  return (measures || []).map((m) => {
    const snapM = m.key ? byKey.get(m.key) : null;
    if (!snapM) return m;
    return {
      ...m,
      name: m.name || snapM.name || m.key || '',
      description: m.description || snapM.description || '',
      category: m.category || snapM.category || '',
      priority: m.priority || snapM.priority || '',
      budget: (m.budget !== undefined && m.budget !== null && m.budget !== 0) ? Number(m.budget) : (Number(snapM.budget) || 0),
      estimatedEffort: snapEffort(m) || snapEffort(snapM),
      responsiblePerson: m.responsiblePerson || snapM.responsiblePerson || snapM.responsibleParty || '',
      deadline: m.deadline || m.echeance || m.dueDate || snapM.deadline || snapM.echeance || snapM.dueDate || '',
    };
  });
};

// Apply persisted inline-edit overrides on top of computed finals.
export const applyFinalMeasureOverrides = (finalMeasures = [], overrides) => {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return finalMeasures;
  return (finalMeasures || []).map((m) => {
    const ov = overrides[m.key];
    if (!ov || typeof ov !== 'object') return m;
    const merged = { ...m };
    for (const field of FINAL_EDITABLE_FIELDS) {
      if (ov[field] !== undefined) merged[field] = ov[field];
    }
    merged.budget = Number(merged.budget) || 0;
    merged.estimatedEffort = Number(merged.estimatedEffort) || 0;
    return merged;
  });
};

// Compute live or validated final PTR output (finalMeasures & coverageMap).
// Includes fused measures, covering measures, explicit keeps, and all implicit non-fused measures.
export const computeFinalOutput = (proposals = [], snapshot = []) => {
  const byKey = new Map();
  (snapshot || []).forEach((snap) => {
    (snap.measures || []).forEach((m) => {
      const name = m.name || m.title || m.measureName || m.label || '';
      byKey.set(m.key, { ...m, name, analysisName: snap.analysisName });
    });
  });

  const decided = (proposals || []).filter((p) => p.status === 'accepted' || p.status === 'edited');
  const referenced = new Set();
  const finalMeasures = [];
  const coverageMap = [];

  for (const p of decided) {
    const keys = (p.sourceMeasureKeys || []).filter((k) => byKey.has(k));
    if (p.type === 'fuse' && p.fusedMeasure && keys.length) {
      keys.forEach((k) => referenced.add(k));
      finalMeasures.push({
        key: `fusion::${p.id}`,
        name: p.fusedMeasure.name || '',
        description: p.fusedMeasure.description || '',
        category: p.fusedMeasure.category || '',
        priority: formatPriority(p.fusedMeasure.priority),
        budget: Number(p.fusedMeasure.budget) || 0,
        estimatedEffort: Number(p.fusedMeasure.estimatedEffort) || 0,
        responsiblePerson: p.fusedMeasure.responsiblePerson || '',
        deadline: p.fusedMeasure.deadline || '',
        origin: 'fused',
        proposalId: p.id,
        justification: p.justification || '',
        sourceMeasureKeys: keys,
        sources: keys.map((k) => ({ key: k, name: byKey.get(k).name, analysisName: byKey.get(k).analysisName })),
      });
      keys.forEach((k) => coverageMap.push({
        coveredKey: k,
        coveredName: byKey.get(k).name || '',
        byProposalId: p.id,
        reason: p.justification || '',
      }));
    } else if (p.type === 'keep') {
      keys.forEach((k) => referenced.add(k));
      keys.forEach((k) => {
        const m = byKey.get(k);
        finalMeasures.push({
          key: k,
          name: m.name || '',
          description: m.description || '',
          category: m.category || '',
          priority: formatPriority(m.priority),
          budget: Number(m.budget) || 0,
          estimatedEffort: snapEffort(m),
          responsiblePerson: snapOwner(m),
          deadline: snapDeadline(m),
          origin: 'kept',
          proposalId: p.id,
          justification: p.justification || '',
          sourceMeasureKeys: [k],
          sources: [{ key: k, name: m.name, analysisName: m.analysisName }],
        });
      });
    } else if (p.type === 'cover' && keys.length >= 2) {
      const covering = keys[keys.length - 1];
      const coveringAlreadyUsed = referenced.has(covering);
      keys.forEach((k) => referenced.add(k));
      keys.slice(0, -1).forEach((k) => coverageMap.push({
        coveredKey: k,
        coveredName: byKey.get(k).name || '',
        byProposalId: p.id,
        reason: p.justification || '',
      }));
      if (!coveringAlreadyUsed) {
        const m = byKey.get(covering);
        finalMeasures.push({
          key: covering,
          name: m.name || '',
          description: m.description || '',
          category: m.category || '',
          priority: formatPriority(m.priority),
          budget: Number(m.budget) || 0,
          estimatedEffort: snapEffort(m),
          responsiblePerson: snapOwner(m),
          deadline: snapDeadline(m),
          origin: 'covering',
          proposalId: p.id,
          justification: p.justification || '',
          sourceMeasureKeys: [covering],
          sources: [{ key: covering, name: m.name, analysisName: m.analysisName }],
        });
      }
    }
  }

  // Implicit keeps (all non-fused measures never referenced in any proposal)
  for (const [key, m] of byKey) {
    if (!referenced.has(key)) {
      finalMeasures.push({
        key,
        name: m.name || '',
        description: m.description || '',
        category: m.category || '',
        priority: formatPriority(m.priority),
        budget: Number(m.budget) || 0,
        estimatedEffort: snapEffort(m),
        responsiblePerson: snapOwner(m),
        deadline: snapDeadline(m),
        origin: 'kept',
        proposalId: null,
        justification: '',
        sourceMeasureKeys: [key],
        sources: [{ key, name: m.name, analysisName: m.analysisName }],
      });
    }
  }

  return { finalMeasures, coverageMap };
};


